import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import styled from 'styled-components';

const IndustrySection = styled(Box)`
  padding: 140px 0;
  position: relative;
  background: #000B18;
  overflow: hidden;
  min-height: 100vh;
`;

const BackgroundPattern = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    linear-gradient(90deg, rgba(0, 229, 255, 0.01) 1px, transparent 1px),
    linear-gradient(0deg, rgba(0, 229, 255, 0.01) 1px, transparent 1px);
  background-size: 40px 40px;
  transform: perspective(1000px) rotateX(60deg) scale(6);
  transform-origin: center 0;
  opacity: 0.1;
`;

const ParticleEffect = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 1;
  background: radial-gradient(circle at 50% 50%, rgba(0, 229, 255, 0.03) 0%, transparent 50%);
`;

const GlowingOrb = styled(motion.div)`
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  z-index: 1;
  background: radial-gradient(circle at center, rgba(0, 229, 255, 0.15) 0%, transparent 70%);
`;

const SectionTitle = styled(motion.h2)`
  font-size: 4rem;
  font-weight: 700;
  color: #fff;
  text-align: center;
  margin-bottom: 0.5rem;
  background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 6rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.8;
  padding: 0 2rem;
`;

const IndustryCard = styled(motion.div)<{ $color: string }>`
  background: rgba(0, 11, 24, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 229, 255, 0.1);
  border-radius: 24px;
  padding: 3.5rem 3rem;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  gap: 2rem;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, \${props => props.$color});
    opacity: 0.1;
    z-index: 0;
    transition: opacity 0.5s ease;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), 
                rgba(0, 229, 255, 0.1) 0%, 
                transparent 50%);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-8px);
    border-color: rgba(0, 229, 255, 0.3);
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.4),
      0 0 50px rgba(0, 229, 255, 0.1);

    &::before {
      opacity: 0.2;
    }

    &::after {
      opacity: 1;
    }
  }
`;

const IconWrapper = styled(motion.div)`
  width: 72px;
  height: 72px;
  position: relative;
  z-index: 2;
  
  svg {
    width: 100%;
    height: 100%;
    filter: drop-shadow(0 0 8px rgba(0, 229, 255, 0.3));
  }
`;

const IndustryTitle = styled(Typography)`
  font-size: 2rem;
  font-weight: 600;
  color: #fff;
  position: relative;
  z-index: 2;
  line-height: 1.3;
  letter-spacing: -0.02em;

  br {
    display: block;
    content: '';
    margin-top: 0.2em;
  }
`;

const IndustryDescription = styled(Typography)`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  line-height: 1.6;
  position: relative;
  z-index: 2;
  margin: 0;
`;

const StatBox = styled(motion.div)`
  background: rgba(0, 229, 255, 0.1);
  border-radius: 12px;
  padding: 1rem 1.5rem;
  color: #00e5ff;
  font-weight: 600;
  font-size: 1.1rem;
  display: inline-block;
  position: relative;
  z-index: 2;
  backdrop-filter: blur(5px);
  box-shadow: 0 4px 15px rgba(0, 229, 255, 0.1);
  transition: all 0.3s ease;
  margin-top: auto;

  &:hover {
    background: rgba(0, 229, 255, 0.15);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 229, 255, 0.15);
  }
`;

const industries = [
  {
    id: 'digital',
    title: 'Digital\nTransformation',
    description: 'Enterprise modernization, process automation, digital strategy',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 15C3 17.8284 3 19.2426 3.87868 20.1213C4.75736 21 6.17157 21 9 21H15C17.8284 21 19.2426 21 20.1213 20.1213C21 19.2426 21 17.8284 21 15" stroke="#4facfe" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12 3V16M12 16L16 11.625M12 16L8 11.625" stroke="#4facfe" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    stats: '47% efficiency increase',
    color: 'from-blue-500/20 to-cyan-500/20'
  },
  {
    id: 'security',
    title: 'Security &\nCompliance',
    description: 'Security architecture, threat detection, regulatory compliance',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#a78bfa" strokeWidth="1.5"/>
        <path d="M15 9L15 15M9 9L9 15M15 12L9 12" stroke="#a78bfa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    stats: '94% threat detection',
    color: 'from-purple-500/20 to-pink-500/20'
  },
  {
    id: 'fintech',
    title: 'Financial\nTechnology',
    description: 'Advanced Risk analytics, algorithmic trading, fraud detection systems',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 11L6.29289 6.70711C6.68342 6.31658 7.31658 6.31658 7.70711 6.70711L10.2929 9.29289C10.6834 9.68342 11.3166 9.68342 11.7071 9.29289L16.2929 4.70711C16.6834 4.31658 17.3166 4.31658 17.7071 4.70711L22 9M22 9V4M22 9H17" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2 17L6.29289 12.7071C6.68342 12.3166 7.31658 12.3166 7.70711 12.7071L10.2929 15.2929C10.6834 15.6834 11.3166 15.6834 11.7071 15.2929L16.2929 10.7071C16.6834 10.3166 17.3166 10.3166 17.7071 10.7071L22 15M22 15V20M22 15H17" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    stats: '32% risk reduction',
    color: 'from-emerald-500/20 to-teal-500/20'
  },
  {
    id: 'hr',
    title: 'HR\nInnovation',
    description: 'Talent analytics, employee engagement, intelligent recruitment',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="#fbbf24" strokeWidth="1.5"/>
        <path d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z" stroke="#fbbf24" strokeWidth="1.5"/>
      </svg>
    ),
    stats: '53% improved talent retention',
    color: 'from-amber-500/20 to-yellow-500/20'
  },
  {
    id: 'legal',
    title: 'Legal\nTechnology',
    description: 'Contract intelligence, compliance automation, case analytics',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2Z" stroke="#4facfe" strokeWidth="1.5"/>
        <path d="M13 7.5H11V12H13" stroke="#4facfe" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 16.5H8" stroke="#4facfe" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    stats: '78% faster contract review',
    color: 'from-blue-500/20 to-indigo-500/20'
  },
  {
    id: 'consumerSafety',
    title: 'Consumer\nSafety',
    description: 'Product transparency, ingredient analysis, regulatory compliance, clean label verification',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 12L11 14L15 10" stroke="#14b8a6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#14b8a6" strokeWidth="1.5"/>
        <path d="M7.5 4.5C8.5 6.5 10.5 7 12 7C13.5 7 15.5 6.5 16.5 4.5" stroke="#14b8a6" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    stats: '86% improved consumer trust',
    color: 'from-teal-500/20 to-green-500/20'
  }
];

const IndustryTransformation = () => {
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    card.style.setProperty('--mouse-x', `${x}%`);
    card.style.setProperty('--mouse-y', `${y}%`);
  };

  return (
    <IndustrySection id="industries">
      <BackgroundPattern />
      <ParticleEffect
        animate={{
          background: [
            'radial-gradient(circle at 30% 30%, rgba(0, 229, 255, 0.03) 0%, transparent 50%)',
            'radial-gradient(circle at 70% 70%, rgba(0, 229, 255, 0.03) 0%, transparent 50%)',
            'radial-gradient(circle at 30% 30%, rgba(0, 229, 255, 0.03) 0%, transparent 50%)',
          ]
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: 'linear'
        }}
      />
      
      <GlowingOrb
        style={{
          width: '600px',
          height: '600px',
          top: '10%',
          right: '-10%',
          opacity: 0.5
        }}
        animate={{
          y: [0, -30, 0],
          opacity: [0.5, 0.3, 0.5],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut'
        }}
      />
      
      <GlowingOrb
        style={{
          width: '400px',
          height: '400px',
          bottom: '10%',
          left: '-5%',
          opacity: 0.3
        }}
        animate={{
          y: [0, 30, 0],
          opacity: [0.3, 0.5, 0.3],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut'
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          Industry
          <br />
          Transformation
        </SectionTitle>
        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          Our industry-specific expertise allows us to develop tailored solutions
          <br />
          that address the unique challenges and opportunities in your sector.
        </SectionSubtitle>

        <Grid container spacing={4}>
          {industries.map((industry, index) => (
            <Grid item xs={12} md={6} lg={4} key={industry.id}>
              <IndustryCard
                $color={industry.color}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
                onMouseMove={handleMouseMove}
              >
                <IconWrapper
                  whileHover={{ 
                    scale: 1.1, 
                    rotate: 5,
                    filter: 'drop-shadow(0 0 12px rgba(0, 229, 255, 0.5))'
                  }}
                  transition={{ 
                    type: 'spring', 
                    stiffness: 300,
                    damping: 10
                  }}
                >
                  {industry.icon}
                </IconWrapper>
                <IndustryTitle variant="h3">
                  {industry.title.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i === 0 && <br />}
                    </React.Fragment>
                  ))}
                </IndustryTitle>
                <IndustryDescription>
                  {industry.description}
                </IndustryDescription>
                <StatBox
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                  viewport={{ once: true }}
                >
                  {industry.stats}
                </StatBox>
              </IndustryCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </IndustrySection>
  );
};

export default IndustryTransformation; 